import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { updatePageMeta } from '../utils/seo';
import { metaFor } from '../seo/routeMeta';
import { BUSINESS } from '../utils/brand';
import './LegalPage.css';

/**
 * Privacy policy. Drafted 2026-09-21 from what the site actually collects and
 * which services it actually loads (see HANDOFF §4d.3 — Thomas handles
 * attorney review). If you add a form field, a tracker or a processor, update
 * this page in the same pack.
 */
function PrivacyPolicy() {
  useEffect(() => {
    updatePageMeta(metaFor('/privacy-policy'));
  }, []);

  return (
    <div className="legal-page">
      <div className="lp-inner">
        <h1>Privacy Policy</h1>
        <p className="lp-date">Effective September 21, 2026</p>

        <p>
          Utah Realty Photos is a real estate photography business based in Eagle Mountain, Utah. This policy
          explains what we collect through utahrealtyphotos.com, why, who helps us process it, and the choices you have.
        </p>

        <h2>What we collect</h2>
        <ul>
          <li><strong>When you book:</strong> your name, email, phone, company, the property address and details (such as square footage, bedrooms and bathrooms), your package and add-ons, and any notes or access instructions you choose to give us.</li>
          <li><strong>When you create an account:</strong> your name, email, phone and a password, which we store only as a one-way hash.</li>
          <li><strong>When you contact us:</strong> what you enter in the contact form.</li>
          <li><strong>When you pay:</strong> invoice and payment records. Card details go directly to Stripe; we never see or store your card number.</li>
          <li><strong>When you enquire about a listing</strong> on a property page: your name, contact details and message, which go to that listing&rsquo;s agent.</li>
          <li><strong>Automatically:</strong> basic technical data such as your browser, device, pages visited and IP address, through the services described below.</li>
        </ul>

        <h2>How we use it</h2>
        <ul>
          <li>To schedule and deliver your shoot, host your gallery and send your invoice and receipts.</li>
          <li>To run your account and answer your questions.</li>
          <li>To protect our forms from spam and abuse, and to find and fix errors.</li>
          <li>To understand site traffic and whether our advertising works, if you allow analytics and ads cookies.</li>
          <li>To send occasional marketing email to real estate professionals. Every marketing email has an unsubscribe link, and we honour it.</li>
        </ul>
        <p>We also keep business contact details for real estate agents drawn from public listings and professional directories, so we can offer our services. You can ask us to remove yours at any time.</p>

        <h2>Who processes it for us</h2>
        <p>We do not sell your personal information. We share it only with the services that run this business on our behalf:</p>
        <div className="lp-table">
          <table>
            <thead><tr><th>Service</th><th>What it does</th></tr></thead>
            <tbody>
              <tr><td>Stripe</td><td>Card payments</td></tr>
              <tr><td>Google</td><td>Email delivery, Google Analytics, Google Ads measurement, and reCAPTCHA spam protection</td></tr>
              <tr><td>Cloudinary</td><td>Photo and video hosting for galleries</td></tr>
              <tr><td>Matterport</td><td>Hosting for 3D tours</td></tr>
              <tr><td>MongoDB Atlas, Railway and Vercel</td><td>Our database and website hosting</td></tr>
              <tr><td>Sentry</td><td>Error reports, so we can fix problems</td></tr>
            </tbody>
          </table>
        </div>
        <p>We may also disclose information if the law requires it, or to protect our rights or someone&rsquo;s safety.</p>

        <h2>How long we keep it</h2>
        <ul>
          <li>Galleries stay online for a set period after delivery, shown on each gallery, unless extended.</li>
          <li>Booking, invoice and payment records are kept as long as we need them for accounting and tax purposes.</li>
          <li>Other information is kept while your account is open or while we need it to serve you, then deleted on request.</li>
        </ul>

        <h2>Cookies</h2>
        <p>See the <Link to="/cookie-policy">Cookie Policy</Link> for what we store in your browser and how to turn off analytics and ads cookies.</p>

        <h2>Your choices</h2>
        <ul>
          <li>Ask us for a copy of the personal information we hold about you, or to correct or delete it.</li>
          <li>Unsubscribe from marketing email with the link in any message.</li>
          <li>Decline analytics and ads cookies on the <Link to="/cookie-policy">Cookie Policy</Link> page.</li>
        </ul>
        <p>We may need to keep some records, such as invoices, where the law requires it.</p>

        <h2>Security</h2>
        <p>The site runs over HTTPS, passwords are stored only as hashes, and admin access is restricted and supports two-factor sign-in. No system is perfectly secure, so please tell us if you notice anything wrong.</p>

        <h2>Children</h2>
        <p>This site is for real estate professionals and property owners. It is not directed to children under 13, and we do not knowingly collect their information.</p>

        <h2>Changes</h2>
        <p>If we change this policy, we will update the date at the top of this page.</p>

        <h2>Contact</h2>
        <p>
          Email <a href={`mailto:${BUSINESS.email}`}>{BUSINESS.email}</a> or call{' '}
          <a href={`tel:${BUSINESS.phone.replace(/[^0-9+]/g, '')}`}>{BUSINESS.phone}</a>.
        </p>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
